import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, Typography, styled } from "@mui/material";
import { SignatureConfigurationNode } from "entities/ApiModel/SignatureConfigurationNode";
import { useConfigurationProvider } from "pages/signatures/providers/manage-signature/ConfigurationProvider";
import { borderRadius, colors, fontSize, fontWeight, gradients, iconSize, margin, padding } from "theme/defaultStyle";
import RecursiveTreeView from "../tree-view";
import { StewartDialog } from "controls/global/dialogs/stewart-dialog/StewartDialog";
import CloseIcon from "theme/icons/CloseIcon";
import AgencyIcon from "theme/icons/AgencyIcon";
import AddIcon from "theme/icons/AddIcon";
import { useSignatureDispatchProvider, useSignatureProvider } from "pages/signatures/providers/manage-signature/SignatureProvider";
import ActionButton from "controls/global/action-button/ActionButton";
import { cloneDeep } from "lodash";
import { useSignatures } from "utils/context/SignaturesContext";
import { showToast } from "controls/global/stewart-toast/showToast";
import { UIConstants } from "utils/data/enum";
import { getDuplicateAgencies, getSelectedAgencyNames } from "../signature-dialog/utils";
import _ from "lodash";
import { hasSelectedNodes as checkHasSelectedNodes } from "utils/signature/treeView";

interface SignatureConfigurationProps {
  nodes?: SignatureConfigurationNode[] | null;
  onClose?: () => void;
  showNext?: boolean;
  onNext?: () => void;
  title?: string;
  setFlowStep?: (step: string) => void;
  setShowSignatureConfig?: (show: boolean) => void;
  isSignatureConfigurationDisabled?: boolean;
  refreshPage?: () => void;
}

const StyledDialog = styled(StewartDialog)({
  "& .MuiDialog-paper": {
    maxHeight: "95vh",
    maxWidth: "50vw",
    minWidth: "45vw",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
});

const SignatureErrorMessage = styled(Typography)({
  color: colors.red01,
  fontSize: fontSize.medium,
  fontWeight: fontWeight.normal1,
  padding: `${padding.zero} ${padding.zero} ${padding.xlarge1} ${padding.xlarge22}`,
  marginTop: margin.xlarge1,
});

const Footer = styled(Box)({
  padding: "20px 32px",
  display: "flex",
  justifyContent: "flex-end",
  gap: "8px"
});

const StyledDialogTitle = styled(Box)({
  display: "flex",
  alignItems: "center",
  columnGap: "24px",
  padding: "28px 32px 20px 32px",
});

const IconContainer = styled("div")({
  background: gradients.blueGradient09,
  borderRadius: borderRadius.large,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  width: "56px",
  height: "56px",
  "& svg": {
    color: colors.white,
    fontSize: iconSize.medium2,
  },
});

const CloseIconContainer = styled("div")({
  marginLeft: "auto",
  width: "32px",
  height: "32px",
  borderRadius: borderRadius.xlarge1,
  background: colors.grey10,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const Content = styled(Box)({
  flex: 1,
  overflowY: "auto",
  padding: `${padding.xlarge1} ${padding.xlarge21} ${padding.zero} ${padding.xlarge21}`,
});

const SubTitleText = styled(Typography)({
  color: "#212121",
  fontSize: "14px",
  lineHeight: "normal",
  fontWeight: 400,
});

const TitleBlock = styled(Box)({
  display: "flex",
  flexDirection: "column",
  rowGap: "24px",
  top: "20px"
});

const TitleText = styled(Typography)({
  fontSize: fontSize.xlarge1,
  fontWeight: fontWeight.bold1,
  marginTop: margin.xlarge40,
});

export default function SignatureConfiguration({
  nodes,
  onClose,
  showNext = false,
  title,
  setFlowStep,
  setShowSignatureConfig,
  isSignatureConfigurationDisabled = false,
  refreshPage,
}: SignatureConfigurationProps) {
  const { dispatch, state: { hasSelectedNodes, nodes: agencyNodes } } = useConfigurationProvider();
  const { dispatch: signatureDispatch } = useSignatureDispatchProvider();
  const { state: signatureState } = useSignatureProvider();
  const [
    { cachedSignature }, { saveSignature, checkName }
  ] = useSignatures();

  // Track initial state for comparison (only in edit mode)
  const [initialNodes, setInitialNodes] = useState<SignatureConfigurationNode | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [isNameValid, setIsNameValid] = useState(true);
  const [duplicatedAgencies, setDuplicatedAgencies] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const initialConfigTreeRef = useRef<SignatureConfigurationNode | null>(null);
  const previousOpenStateRef = useRef(signatureState?.open);

  // Store initial state when component mounts (only in edit mode)
  useEffect(() => {
    if (!showNext && nodes && nodes.length > 0) {
      const clonedNodes = cloneDeep(nodes[0]);
      setInitialNodes(clonedNodes);
      initialConfigTreeRef.current = cloneDeep(nodes[0]);
    }
  }, [showNext, nodes]);

  // Compare current state with initial state to detect changes (only in edit mode)
  useEffect(() => {
    // Skip comparison if in add mode (showNext = true)
    if (showNext) {
      setHasChanges(false);
      return;
    }

    if (!initialNodes || !agencyNodes) {
      setHasChanges(false);
      return;
    }

    // Compare the nodes - if they are equal, there are NO changes
    const areEqual = _.isEqual(agencyNodes, initialNodes);
    setHasChanges(!areEqual);

  }, [agencyNodes, initialNodes, showNext]);

  const onDataChanged = (data: SignatureConfigurationNode) => {
    dispatch({ type: "DATA_CHANGED", data: cloneDeep(data) });

    // Validate on data change for edit mode
    if (!showNext && cachedSignature) {
      validateSignatureNameForEditMode(data, cachedSignature.signatureImageName);
    }
    // Validate on data change for add mode if signature name exists in provider
    else if (showNext) {
      validateSignatureNameForAddMode(data);
    }
  };

  const validateSignatureNameForEditMode = async (configData: SignatureConfigurationNode, signatureName: string) => {
    if (!cachedSignature || !signatureName || signatureName.trim() === "") return;

    setIsValidating(true);

    try {
      const payload = {
        ...cachedSignature,
        signatureImageName: signatureName.trim(),
        signatureConfiguration: configData,
      };

      const signatureDetails = await checkName(payload);

      if (!signatureDetails || !signatureDetails.signatureConfiguration) {
        setIsNameValid(false);
        setIsValidating(false);
        return;
      }

      const duplicateAgencies = getDuplicateAgencies(
        signatureDetails.signatureConfiguration
      );

      // No duplicates found - name is valid
      if (duplicateAgencies.length === 0) {
        setIsNameValid(true);
        setDuplicatedAgencies([]);
        setIsValidating(false);
        return;
      }

      const hasAllMyAgencies = duplicateAgencies.some(
        (agency) => agency.agencyName === "All My Agencies"
      );

      // If name hasn't changed and has "All My Agencies", allow it
      if (hasAllMyAgencies) {
        setIsNameValid(true);
        setDuplicatedAgencies([]);
        setIsValidating(false);
        return;
      }

      const initialAgencyNames = initialConfigTreeRef.current
        ? getSelectedAgencyNames(initialConfigTreeRef.current)
        : [];

      const duplicatesInNewlySelectedAgencies = duplicateAgencies.filter((da) => {
        return !initialAgencyNames.includes(da.agencyName);
      });

      if (duplicatesInNewlySelectedAgencies.length === 0) {
        setIsNameValid(true);
        setDuplicatedAgencies([]);
        setIsValidating(false);
        return;
      }

      // Name has duplicates in selected agencies
      setIsNameValid(false);
      setDuplicatedAgencies(
        duplicatesInNewlySelectedAgencies.map((da) => da.agencyName)
      );
      setIsValidating(false);
    } catch (error) {
      console.error("Error validating signature name:", error);
      setIsNameValid(false);
      setIsValidating(false);
    }
  };

  const validateSignatureNameForAddMode = async (configData: SignatureConfigurationNode) => {
    const signatureName = signatureState?.signature?.name;

    if (!signatureName || signatureName.trim() === "") {
      setIsNameValid(true);
      setDuplicatedAgencies([]);
      return;
    }

    setIsValidating(true);

    try {
      const payload = {
        signatureImageID: 0,
        signatureImageName: signatureName.trim(),
        signatureConfiguration: configData,
      };

      const signatureDetails = await checkName(payload);

      if (!signatureDetails || !signatureDetails.signatureConfiguration) {
        setIsNameValid(false);
        setIsValidating(false);
        return;
      }

      const duplicateAgencies = getDuplicateAgencies(
        signatureDetails.signatureConfiguration
      );

      // No duplicates found - name is valid
      if (duplicateAgencies.length === 0) {
        setIsNameValid(true);
        setDuplicatedAgencies([]);
        setIsValidating(false);
        return;
      }

      const hasAllMyAgencies = duplicateAgencies.some(
        (agency) => agency.agencyName === "All My Agencies"
      );

      const selectedAgencyNames = getSelectedAgencyNames(
        payload.signatureConfiguration
      );

      // If "All My Agencies" is a duplicate, all selected agencies are duplicates
      if (hasAllMyAgencies) {
        setIsNameValid(false);
        setDuplicatedAgencies(selectedAgencyNames);
        setIsValidating(false);
        return;
      }

      const duplicateInSelectedAgencies = duplicateAgencies.filter((da) => {
        return selectedAgencyNames.includes(da.agencyName);
      });

      // No duplicates in selected agencies
      if (duplicateInSelectedAgencies.length === 0) {
        setIsNameValid(true);
        setDuplicatedAgencies([]);
        setIsValidating(false);
        return;
      }

      // Name has duplicates in selected agencies
      setIsNameValid(false);
      setDuplicatedAgencies(
        duplicateInSelectedAgencies.map((da) => da.agencyName)
      );
      setIsValidating(false);
    } catch (error) {
      setIsNameValid(false);
      setIsValidating(false);
    }
  };

  const handleSignatureDialogBack = () => {
    // Hide the signature dialog without clearing data
    signatureDispatch({ type: "HIDE_SIGNATURE_DIALOG" });
    setFlowStep?.("ADD_SIGNATURE");
    setShowSignatureConfig?.(true);
  };

  useEffect(() => {
    if (!showNext) return;
    
    const wasOpen = previousOpenStateRef.current;
    const isNowClosed = !signatureState?.open;
    
    previousOpenStateRef.current = signatureState?.open;
    
    if (wasOpen && isNowClosed && agencyNodes && signatureState?.signature?.name) {
      validateSignatureNameForAddMode(agencyNodes);
    }
  }, [signatureState?.open, showNext, agencyNodes, signatureState?.signature?.name]);

  const handleAddSignature = () => {
    signatureDispatch({
      type: "OPEN", mode: "ADD", onBackToConfiguration: handleSignatureDialogBack
    });
  }
  const updateAgencySetup = async () => {
    if (!cachedSignature) {
      console.error("No signature to update");
      return;
    }

    if (!agencyNodes) {
      console.error("No agency nodes to update");
      return;
    }

    if (!isNameValid || isSaving) {
      return;
    }

    setIsSaving(true);

    try {
      const result = await saveSignature({
        ...cachedSignature,
        signatureConfiguration: agencyNodes,
      });

      if (result?.success) {
        showToast({
          type: 'success',
          title: UIConstants.AGENCY_UPDATE_SUCCESS_MESSAGE.toString(),
        });
        onClose?.();
        // await getSignatures();
        refreshPage && refreshPage();

      } else {
        showToast({
          type: 'error',
          title: UIConstants.AGENCY_UPDATE_ERROR_MESSAGE.toString(),
      });
      }
    } catch (error) {
      console.error("Error updating signature:", error);
      showToast({
        type: 'error',
        title: UIConstants.AGENCY_UPDATE_ERROR_MESSAGE.toString(),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    signatureDispatch({type: "CLOSE"});
    if (!showNext) {
      setInitialNodes(null);
      setHasChanges(false);
    }

    setIsNameValid(true);
    setDuplicatedAgencies([]);

    onClose?.();
  };

  return (
    <StyledDialog
      open
      onClose={handleClose}
    >
      <StyledDialogTitle>
        <IconContainer>{!showNext ? <AgencyIcon /> : <AddIcon />}</IconContainer>

        <TitleBlock>
          <TitleText>{title ?? "View/Edit Agency Access"}</TitleText>

          <SubTitleText>
            {showNext
              ? "Select the Agency, State, or Agency Locations where this signature should be available."
              : "Update the Agency, State, or Agency Locations where this signature is available."}
          </SubTitleText>
        </TitleBlock>

        <CloseIconContainer>
          <IconButton
            size="small"
            onClick={handleClose}
            sx={{ padding: 0 }}
          >
            <CloseIcon />
          </IconButton>
        </CloseIconContainer>
      </StyledDialogTitle>

      <Content>
        {nodes && nodes.length > 0 ? (
          <RecursiveTreeView
            data={agencyNodes ? [agencyNodes] : nodes}
            onDataChanged={onDataChanged}
            highlightSelected={true}
            isSignatureConfigurationDisabled={isSignatureConfigurationDisabled}
            duplicatedAgencies={duplicatedAgencies}
          />
        ) : (
          <Typography sx={{ fontSize: 16, color: colors.grey07 }}>Loading...</Typography>
        )}
      </Content>


      {!isNameValid && duplicatedAgencies.length > 0 && (
        <SignatureErrorMessage>
          *Signature name {`"${showNext ? signatureState?.signature?.name : cachedSignature?.signatureImageName}"`} Already exists for this Agency
        </SignatureErrorMessage>
      )}
      <Footer>
        <ActionButton
          variant="outlined"
          color="secondary"
          onClick={handleClose}
        >
          Cancel
        </ActionButton>
        <ActionButton
          variant="outlined"
          color="primary"
          onClick={showNext ? handleAddSignature : updateAgencySetup}
          disabled={showNext ? (!hasSelectedNodes || !isNameValid || isValidating || isSaving) : (!hasChanges || !isNameValid || isValidating || isSaving || !checkHasSelectedNodes(agencyNodes))}
        >
          {isValidating ? "Validating..." : showNext ? "Next" : " Update Access"}
        </ActionButton>
      </Footer>
    </StyledDialog>
  );
}
